import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Loader2, 
  AlertCircle, 
  RefreshCw, 
  Replace,
  User,
  Building,
  X,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import MuscleBadges from '../common/MuscleBadges';

const ExerciseReplacementPanel = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('schede'); // 'schede' o 'templates'
  
  // Dati schede
  const [schedeData, setSchedeData] = useState([]);
  const [schedeLoading, setSchedeLoading] = useState(false);
  
  // Dati template
  const [templatesData, setTemplatesData] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  // Stato per modal sostituzione
  const [replacingExercise, setReplacingExercise] = useState(null);
  const [replaceSearchQuery, setReplaceSearchQuery] = useState('');
  const [replaceExercises, setReplaceExercises] = useState([]);
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Carica schede
  const fetchSchede = async () => {
    setSchedeLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/admin_schede_with_exercises.php');
      
      if (response.data.success) {
        setSchedeData(response.data.data || []);
      } else {
        setError(response.data.message || 'Errore durante il recupero delle schede');
      }
    } catch (err) {
      console.error('Errore durante il recupero delle schede:', err);
      setError('Errore di connessione al server');
    } finally {
      setSchedeLoading(false);
      setLoading(false);
    }
  };
  
  // Carica template
  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/admin_templates_with_exercises.php');
      
      if (response.data.success) {
        setTemplatesData(response.data.data || []);
      } else {
        setError(response.data.message || 'Errore durante il recupero dei template');
      }
    } catch (err) {
      console.error('Errore durante il recupero dei template:', err);
      setError('Errore di connessione al server');
    } finally {
      setTemplatesLoading(false);
    }
  };
  
  // Carica dati in base alla sezione attiva
  useEffect(() => {
    if (activeSection === 'schede') {
      fetchSchede();
    } else {
      fetchTemplates();
    }
  }, [activeSection]);
  
  // Cerca esercizi per sostituzione
  const searchReplaceExercises = async (query) => {
    if (!query || query.trim() === '') {
      setReplaceExercises([]);
      return;
    }
    
    try {
      setReplaceLoading(true);
      const response = await api.get('/esercizi.php', {
        params: {
          search: query.trim(),
          limit: 20,
          include_muscles: true
        }
      });
      
      // Filtra solo esercizi approvati
      const approvedExercises = Array.isArray(response.data) 
        ? response.data.filter(ex => !ex.status || ex.status === 'approved')
        : [];
      
      // Escludi l'esercizio corrente
      const filteredExercises = approvedExercises.filter(
        ex => ex.id !== replacingExercise?.esercizio_id
      );
      
      setReplaceExercises(filteredExercises);
    } catch (err) {
      console.error('Errore nella ricerca esercizi:', err);
      setReplaceExercises([]);
    } finally {
      setReplaceLoading(false);
    }
  };
  
  // Handler per aprire modal sostituzione
  const handleOpenReplaceModal = (exercise, context) => {
    setReplacingExercise({
      ...exercise,
      context: context // 'scheda' o 'template'
    });
    setReplaceSearchQuery('');
    setReplaceExercises([]);
  };
  
  // Handler per chiudere modal sostituzione
  const handleCloseReplaceModal = () => {
    setReplacingExercise(null);
    setReplaceSearchQuery('');
    setReplaceExercises([]);
  };
  
  // Handler per sostituzione esercizio
  const handleReplaceExercise = async (newExerciseId) => {
    if (!replacingExercise || !newExerciseId) {
      setError('Dati mancanti per la sostituzione');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await api.post('/admin_replace_exercise_in_workouts.php', {
        old_exercise_id: replacingExercise.esercizio_id,
        new_exercise_id: newExerciseId
      });
      
      if (response.data.success) {
        setMessage({
          type: 'success',
          text: response.data.message || 'Esercizio sostituito con successo!'
        });
        
        setTimeout(() => setMessage(null), 5000);
        
        handleCloseReplaceModal();
        
        // Ricarica i dati
        if (activeSection === 'schede') {
          fetchSchede();
        } else {
          fetchTemplates();
        }
      } else {
        setError(response.data.message || 'Errore durante la sostituzione');
      }
    } catch (err) {
      console.error('Errore:', err);
      setError(err.response?.data?.message || 'Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };
  
  // Debounce per ricerca esercizi
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (replacingExercise) {
        searchReplaceExercises(replaceSearchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [replaceSearchQuery, replacingExercise]);
  
  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Sostituzione Esercizi in Schede e Template
        </h2>
        
        <button
          onClick={() => activeSection === 'schede' ? fetchSchede() : fetchTemplates()}
          className="flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 transition"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </button>
      </div>
      
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <p>{message.text}</p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg">
          <p>{error}</p>
        </div>
      )}
      
      {/* Tabs per schede/template */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveSection('schede')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSection === 'schede'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <User className="h-4 w-4 inline mr-2" />
          Schede ({schedeData.length} utenti)
        </button>
        <button
          onClick={() => setActiveSection('templates')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSection === 'templates'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Building className="h-4 w-4 inline mr-2" />
          Template ({templatesData.length} palestre)
        </button>
      </div>
      
      {/* Contenuto */}
      {loading || (activeSection === 'schede' ? schedeLoading : templatesLoading) ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : activeSection === 'schede' ? (
        <SchedeSection 
          data={schedeData} 
          onReplaceExercise={handleOpenReplaceModal}
        />
      ) : (
        <TemplatesSection 
          data={templatesData} 
          onReplaceExercise={handleOpenReplaceModal}
        />
      )}
      
      {/* Modal per sostituzione esercizio */}
      {replacingExercise && (
        <ReplaceExerciseModal
          exercise={replacingExercise}
          searchQuery={replaceSearchQuery}
          setSearchQuery={setReplaceSearchQuery}
          exercises={replaceExercises}
          loading={replaceLoading}
          onReplace={handleReplaceExercise}
          onClose={handleCloseReplaceModal}
        />
      )}
    </div>
  );
};

// Componente per sezione schede
const SchedeSection = ({ data, onReplaceExercise }) => {
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  
  const toggleUser = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };
  
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Nessuna scheda trovata
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {data.map((user) => {
        const isExpanded = expandedUsers.has(user.user_id);
        
        return (
          <div
            key={user.user_id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <div 
              className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              onClick={() => toggleUser(user.user_id)}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {user.user_name || user.username || `Utente #${user.user_id}`}
                  </h3>
                  {user.email && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {user.schede.length} {user.schede.length === 1 ? 'scheda' : 'schede'}
                  </p>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>
            
            {isExpanded && (
              <div className="p-4 space-y-4">
                {user.schede.map((scheda) => (
                  <div
                    key={scheda.scheda_id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                      {scheda.scheda_nome}
                    </h4>
                    {scheda.scheda_descrizione && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {scheda.scheda_descrizione}
                      </p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {scheda.esercizi.map((esercizio) => (
                        <ExerciseCard
                          key={esercizio.scheda_esercizio_id}
                          exercise={esercizio}
                          onReplace={() => onReplaceExercise(esercizio, 'scheda')}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Componente per sezione template
const TemplatesSection = ({ data, onReplaceExercise }) => {
  const [expandedGyms, setExpandedGyms] = useState(new Set());
  
  const toggleGym = (gymId) => {
    setExpandedGyms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gymId)) {
        newSet.delete(gymId);
      } else {
        newSet.add(gymId);
      }
      return newSet;
    });
  };
  
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Nessun template trovato
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {data.map((gym) => {
        const isExpanded = expandedGyms.has(gym.gym_id);
        
        return (
          <div
            key={gym.gym_id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <div 
              className="p-4 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              onClick={() => toggleGym(gym.gym_id)}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {gym.gym_name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {gym.templates.length} {gym.templates.length === 1 ? 'template' : 'template'}
                  </p>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>
            
            {isExpanded && (
              <div className="p-4 space-y-4">
                {gym.templates.map((template) => (
                  <div
                    key={template.template_id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                      {template.template_name}
                    </h4>
                    {template.template_description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {template.template_description}
                      </p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {template.esercizi.map((esercizio) => (
                        <ExerciseCard
                          key={esercizio.template_exercise_id}
                          exercise={esercizio}
                          onReplace={() => onReplaceExercise(esercizio, 'template')}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Componente card esercizio
const ExerciseCard = ({ exercise, onReplace }) => {
  const imageUrl = exercise.immagine_nome
    ? `/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`
    : null;
  
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
      <div className="flex gap-3">
        {imageUrl && (
          <div className="flex-shrink-0">
            <img
              src={imageUrl}
              alt={exercise.esercizio_nome}
              className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h5 className="font-medium text-gray-900 dark:text-white text-sm">
            {exercise.esercizio_nome}
          </h5>
          {exercise.gruppo_muscolare && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {exercise.gruppo_muscolare}
            </p>
          )}
          <button
            onClick={onReplace}
            className="mt-2 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center gap-1"
          >
            <Replace className="h-3 w-3" />
            Sostituisci
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal per sostituzione esercizio
const ReplaceExerciseModal = ({
  exercise,
  searchQuery,
  setSearchQuery,
  exercises,
  loading,
  onReplace,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Sostituisci Esercizio
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sostituisci "<strong>{exercise.esercizio_nome}</strong>" con un altro esercizio esistente.
          </p>
        </div>
        
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca esercizio per nome..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {searchQuery
                ? 'Nessun esercizio trovato. Prova con un altro termine di ricerca.'
                : 'Inizia a digitare per cercare un esercizio...'}
            </div>
          ) : (
            <div className="space-y-2">
              {exercises.map((ex) => {
                const imageUrl = ex.immagine_nome
                  ? `/api/serve_image.php?filename=${encodeURIComponent(ex.immagine_nome)}`
                  : null;
                
                return (
                  <div
                    key={ex.id}
                    onClick={() => onReplace(ex.id)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start gap-4">
                      {imageUrl && (
                        <div className="flex-shrink-0">
                          <img
                            src={imageUrl}
                            alt={ex.nome}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {ex.nome}
                        </h4>
                        {ex.descrizione && (
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {ex.descrizione}
                          </p>
                        )}
                        <div className="mt-2">
                          <MuscleBadges
                            primaryMuscle={ex.primary_muscle}
                            secondaryMuscles={ex.secondary_muscles}
                            variant="compact"
                          />
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onReplace(ex.id);
                        }}
                        className="ml-4 flex-shrink-0 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
                      >
                        Sostituisci
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseReplacementPanel;

